import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Film, Lock } from "lucide-react";
import { Reveal, SectionTitle } from "@/components/Reveal";
import { VIDEOS, MIN_SLOTS } from "@/data/videos";

const FRAME = { border: "1px solid rgba(139, 0, 0, 0.6)", boxShadow: "0 0 24px rgba(180, 0, 30, 0.35)" };
const pad = (n) => String(n).padStart(2, "0");

const Missing = ({ small }) => (
  <div className="absolute inset-0 grid place-items-center bg-[var(--crypt)] text-center p-3">
    <div>
      <Film className={`${small ? "w-5 h-5" : "w-8 h-8"} text-[var(--gold)] mx-auto`} />
      <div className={`font-display mt-2 ${small ? "text-sm" : "text-xl"}`}>Video coming soon</div>
    </div>
  </div>
);

const Thumb = ({ v, i, active, broken, onPick, onBroken }) => (
  <button
    data-testid={`video-thumb-${v.id}`}
    onClick={() => onPick(i)}
    aria-label={`Play ${v.title}`}
    className="group text-left shrink-0 w-[68%] sm:w-auto snap-start"
  >
    <div className={`relative aspect-video rounded-xl overflow-hidden bg-black border transition-[border-color,box-shadow] duration-300 ${active ? "border-[var(--blood)] shadow-[0_0_20px_rgba(224,38,58,0.45)]" : "border-[var(--line)] group-hover:border-[var(--gold)]/60"}`}>
      {broken ? <Missing small /> : (
        <video src={`${v.src}#t=0.1`} poster={v.poster} muted playsInline preload="metadata" onError={() => onBroken(v.id)} className="w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
      )}
      {!broken && <div className="absolute inset-0 grid place-items-center">
        <span className={`w-11 h-11 rounded-full grid place-items-center backdrop-blur-sm transition-colors ${active ? "bg-[var(--blood)]" : "bg-black/60 group-hover:bg-[var(--blood)]"}`}>
          <Play className="w-4 h-4 ml-0.5 fill-current" />
        </span>
      </div>}
      {active && <span className="absolute top-2 left-2 bg-[var(--blood)] px-2 py-0.5 font-mono text-[10px] tracking-widest uppercase">Now playing</span>}
    </div>
    <div className="mt-3 flex items-baseline gap-3">
      <span className="font-mono text-xs text-[var(--blood)]">{pad(i + 1)}</span>
      <span className={`font-display text-base ${active ? "text-[var(--gold)]" : ""}`}>{v.title}</span>
    </div>
    <div className="font-mono text-[11px] tracking-widest uppercase text-[var(--ink-dim)] mt-1 pl-8">{v.tag}</div>
  </button>
);

const Slot = ({ n }) => (
  <div data-testid={`video-slot-${n}`} className="shrink-0 w-[68%] sm:w-auto snap-start">
    <div className="aspect-video rounded-xl border border-dashed border-[var(--gold)]/30 bg-[var(--crypt)] grid place-items-center">
      <Lock className="w-5 h-5 text-[var(--gold)]" />
    </div>
    <div className="mt-3 flex items-baseline gap-3">
      <span className="font-mono text-xs text-[var(--ink-dim)]">{pad(n)}</span>
      <span className="font-display text-base text-[var(--ink-dim)]">Next clip soon</span>
    </div>
  </div>
);

export const Videos = () => {
  const [idx, setIdx] = useState(0);
  const [broken, setBroken] = useState({});
  const player = useRef(null);
  const cur = VIDEOS[idx];
  const markBroken = (id) => setBroken((b) => ({ ...b, [id]: true }));

  const pick = (i) => {
    setIdx(i);
    setTimeout(() => player.current?.play?.().catch(() => {}), 50);
  };
  const next = () => { if (VIDEOS.length > 1) pick((idx + 1) % VIDEOS.length); };
  const slots = Math.max(0, MIN_SLOTS - VIDEOS.length);

  return (
    <section id="videos" data-testid="videos-section" className="relative bg-black py-28 sm:py-40 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-10">
        <Reveal>
          <SectionTitle>Videos</SectionTitle>
        </Reveal>
        <Reveal delay={0.1} className="mt-16">
          <div className="mx-auto w-full max-w-[960px]">
            <div data-testid="videos-player-frame" className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden" style={FRAME}>
              {cur && !broken[cur.id] ? (
                <video
                  key={cur.id}
                  ref={player}
                  data-testid="videos-player"
                  src={cur.src}
                  poster={cur.poster}
                  controls
                  playsInline
                  preload="metadata"
                  onEnded={next}
                  onError={() => markBroken(cur.id)}
                  className="w-full h-full object-contain bg-black"
                />
              ) : <Missing />}
            </div>
            {cur && (
              <motion.div key={cur.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                className="mt-5 flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-3">
                  <span data-testid="videos-counter" className="font-mono text-xs text-[var(--blood)]">{pad(idx + 1)} / {pad(VIDEOS.length)}</span>
                  <span data-testid="videos-current-title" className="font-display text-xl sm:text-2xl">{cur.title}</span>
                </div>
                <span className="font-mono text-xs tracking-widest uppercase text-[var(--gold)]">{cur.tag}</span>
              </motion.div>
            )}
            <p data-testid="videos-caption" className="mt-6 text-center text-sm sm:text-base text-[var(--ink-dim)]">
              Only the royal join the bat cave.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="mt-14">
          <div className="mx-auto max-w-[960px]">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--ink-dim)] mb-5">Pick a clip</div>
            <div data-testid="videos-thumbs" className="flex sm:grid sm:grid-cols-4 gap-4 overflow-x-auto sm:overflow-visible snap-x snap-mandatory scroll-px-4 sm:scroll-px-0 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
              {VIDEOS.map((v, i) => (
                <Thumb key={v.id} v={v} i={i} active={i === idx} broken={broken[v.id]} onPick={pick} onBroken={markBroken} />
              ))}
              {Array.from({ length: slots }).map((_, k) => <Slot key={k} n={VIDEOS.length + k + 1} />)}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
